import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, LogOut, User, ShieldCheck } from "lucide-react";
import { LOGO_URL, COMPANY, NAV_ITEMS } from "@/lib/content";
import { useAuth } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
function BrandMark({ size = 46 }) {
  return (
    <div className="flex items-center" data-testid="brand-mark">
      <img
        src={LOGO_URL}
        alt="VAJRON Global Tech Pvt. Ltd."
        style={{ height: size, width: "auto", objectFit: "contain", display: "block" }}
      />
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const location = useLocation();

  const openLogin = () => { setAuthMode("login"); setAuthOpen(true); };
  const openSignup = () => { setAuthMode("signup"); setAuthOpen(true); };
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-white text-[#0F172A]">
      {/* DESKTOP SIDEBAR — collapsible overlay (closed by default) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 hidden lg:block"
          style={{ background: "rgba(11,17,32,0.35)", backdropFilter: "blur(2px)" }}
          onClick={closeSidebar}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`vj-sidebar hidden lg:flex transition-transform duration-300 ease-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="desktop-sidebar"
      >
        <div className="px-5 pt-6 pb-5 border-b border-slate-200">
          <Link to="/" data-testid="sidebar-brand-link"><BrandMark /></Link>
        </div>
        <nav className="flex-1 pt-5">
          <div className="overline px-5 mb-2">Navigate</div>
          {NAV_ITEMS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `vj-sidebar-link ${isActive ? "active" : ""}`
              }
              end={it.to === "/"}
            >
              <ChevronRight size={14} strokeWidth={2} />
              <span>{it.label}</span>
            </NavLink>
          ))}
          <div className="vj-divider mx-5 my-5" />
          <div className="overline px-5 mb-2">Account</div>
          {user ? (
            <>
              <div className="px-5 py-3 text-sm">
                <div className="flex items-center gap-2 text-[#0F172A]">
                  {user.is_owner ? <ShieldCheck size={14} className="text-emerald-600"/> : <User size={14}/>}
                  <span className="font-medium truncate">{user.name || user.email}</span>
                </div>
                <div className="overline mt-1" style={{ fontSize: 10 }}>
                  {user.is_owner ? "Owner · Aryan Sharma" : "Member"}
                </div>
              </div>
              <button
                onClick={logout}
                data-testid="sidebar-logout-btn"
                className="vj-sidebar-link w-full text-left"
              >
                <LogOut size={14} /> Sign out
              </button>
            </>
          ) : (
            <div className="px-3 pb-6 space-y-2">
              <button
                data-testid="sidebar-login-btn"
                onClick={openLogin}
                className="vj-btn-ghost w-full"
              >
                Log in
              </button>
              <button
                data-testid="sidebar-signup-btn"
                onClick={openSignup}
                className="vj-btn-primary w-full"
              >
                Sign up
              </button>
            </div>
          )}
        </nav>
        <div className="px-5 py-4 border-t border-slate-200">
          <div className="overline" style={{ fontSize: 9 }}>Contact</div>
          <div className="font-mono text-[12px] text-[#0F172A] mt-1">{COMPANY.phone}</div>
        </div>
      </aside>

      {/* MOBILE TOPBAR */}
      <header className="vj-topbar lg:hidden" data-testid="mobile-topbar">
        <div className="flex items-center justify-between px-5 py-3">
          <Link to="/" onClick={() => setMobileOpen(false)} data-testid="topbar-brand-link">
            <BrandMark size={34} />
          </Link>
          <div className="flex items-center gap-2">
            {!user && (
              <button onClick={openLogin} className="vj-btn-ghost" style={{ padding: "8px 14px" }} data-testid="topbar-login-btn">
                Log in
              </button>
            )}
            <button
              className="vj-btn-ghost"
              style={{ padding: "10px 12px" }}
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="border-t border-slate-200 bg-white" data-testid="mobile-menu">
            {NAV_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-${it.testid}`}
                className={({ isActive }) =>
                  `block px-5 py-3 border-b border-slate-100 text-sm ${
                    isActive ? "text-[#0B1120] font-semibold bg-slate-50" : "text-slate-600"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <div className="p-4 flex gap-2 border-t border-slate-200">
              {user ? (
                <button onClick={() => { logout(); setMobileOpen(false); }} className="vj-btn-ghost w-full" data-testid="mobile-logout-btn">
                  <LogOut size={14}/> Sign out ({user.name || user.email})
                </button>
              ) : (
                <button onClick={() => { openSignup(); setMobileOpen(false); }} className="vj-btn-primary w-full" data-testid="mobile-signup-btn">
                  Sign up
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* MAIN */}
      <main>
        {/* Desktop top utility bar with hamburger toggle */}
        <div className="hidden lg:flex items-center justify-between px-10 py-4 border-b border-slate-200 bg-white/90 backdrop-blur sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="vj-btn-ghost"
              style={{ padding: "8px 12px" }}
              data-testid="desktop-sidebar-toggle"
              aria-label="Toggle navigation"
            >
              {sidebarOpen ? <X size={16}/> : <Menu size={16}/>}
            </button>
            <Link to="/" data-testid="desktop-brand-link"><BrandMark size={58}/></Link>
            <span className="overline ml-3">
              {location.pathname === "/" ? "Home" : NAV_ITEMS.find((n) => n.to === location.pathname)?.label || "VAJRON"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-3">
                {user.is_owner && (
                  <span className="vj-chip" data-testid="owner-badge">
                    <ShieldCheck size={12} className="text-emerald-600"/> Owner Mode
                  </span>
                )}
                <span className="text-sm text-slate-600 font-medium">{user.name || user.email}</span>
              </div>
            ) : (
              <>
                <button onClick={openLogin} className="vj-btn-ghost" style={{ padding: "8px 16px" }} data-testid="top-login-btn">
                  Log in
                </button>
                <button onClick={openSignup} className="vj-btn-primary" style={{ padding: "8px 16px" }} data-testid="top-signup-btn">
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>

        <div data-testid="page-content">{children}</div>

        {/* FOOTER */}
        <footer className="vj-dark mt-24" data-testid="site-footer">
          <div className="px-6 lg:px-12 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="VAJRON Global Tech Pvt. Ltd." style={{ height: 48, width: "auto", objectFit: "contain", display: "block" }} />
              </div>
              <p className="mt-5 text-slate-400 text-sm leading-relaxed max-w-md">
                Engineering advanced drones, robotics, autonomous systems, and intelligent platforms for defence,
                government, industry, and global enterprise.
              </p>
              <p className="mt-3 text-slate-500 text-xs">Made in India. Built for the World.</p>
            </div>
            <div>
              <div className="overline text-slate-400">Explore</div>
              <ul className="mt-3 space-y-2 text-sm">
                {NAV_ITEMS.map((n) => (
                  <li key={n.to}><Link to={n.to} className="text-slate-300 hover:text-white" data-testid={`footer-${n.testid}`}>{n.label}</Link></li>
                ))}
                <li><Link to="/verify" className="text-slate-300 hover:text-white" data-testid="footer-verify-link">Credential Verification</Link></li>
              </ul>
            </div>
            <div>
              <div className="overline text-slate-400">Contact</div>
              <div className="mt-3 text-sm text-slate-300">{COMPANY.phone}</div>
              <a href="mailto:info@vajronglobaltech.com" className="mt-1 block text-sm text-slate-300 hover:text-white" data-testid="footer-email">info@vajronglobaltech.com</a>
              <div className="mt-1 text-sm text-slate-500">India · Global Operations</div>
            </div>
          </div>
          <div className="border-t border-slate-800 px-6 lg:px-12 py-5 flex flex-col md:flex-row items-center justify-between text-xs text-slate-500">
            <div>© {new Date().getFullYear()} VAJRON Global Tech Pvt. Ltd. All rights reserved.</div>
            <div className="font-mono mt-2 md:mt-0">CIN · IN-PRIVATE · ENG-AERO-AI</div>
          </div>
        </footer>
      </main>

      <AuthModal
        open={authOpen}
        mode={authMode}
        onClose={() => setAuthOpen(false)}
        switchMode={(m) => setAuthMode(m)}
      />
    </div>
  );
}
